import React, { useState, useEffect } from 'react';
import { AddressView, MapPinEnum, Heading, HeadingEnum, TextColorEnum } from '@desquared/wave-components-library';
import isEmpty from 'lodash/isEmpty';
import Cookies from 'universal-cookie';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import CountryCodeAside from '../Aside/CountryCodeAside';
import { CountryCodeSideSheetStore } from '../../store/sideSheet';
import { CountryCodeStore } from '@/store/countryCode';
import useDisableBodyScroll from '@/hooks/useDisableBodyScroll';
import User from '@/store/user';
import GOOGLE_API_KEY from '@/utils/googleAPIKey';
import { seperateCountryCode } from '@/utils/countriesTransform';
import { Shop } from '@/store/shop';
import useDeliveryServices from '@/hooks/useDeliveryServices';
import { getAddressType } from '@/utils/addressUtilities';
import { hasEmptyKey } from '@/utils/maskEmail';
import { getShopsV3 } from '@/api/Shops';

import { createUserAddress } from '@/api/users';
import { setShopId } from '@/utils/products/products';
import { IsFromLoginStore } from '@/store/isFromLogin';

import styles from './AddressDetailsPage.module.css';

export default function AddressDetailsPage({ selectedAddress = null, user, guestUser, notEligibleWording, config }) {
  const t = useTranslations();
  const router = useRouter();
  const [isNotEligibleText, setIsNotEligibleText] = useState('');
  const [isNotEligible, setIsNotEligible] = useState(false);
  const cookies = new Cookies();

  const token = cookies.get('token');

  const [countryCode, setCountryCode] = useState(['']);
  const [defaultMapCenter, setDefaultMapCenter] = useState({
    lat: process.env.defaultMapCenter.lat,
    lon: process.env.defaultMapCenter.lng,
  });
  const { selectedService, setSelectedUserAddressIndex } = useDeliveryServices();
  useDisableBodyScroll();
  let address;
  if (!isEmpty(guestUser)) {
    if (guestUser?.address) {
      address = guestUser?.address[0];
    } else {
      {
        address = {};
      }
    }
  } else {
    address = user?.addresses?.find(ele => ele._id === selectedAddress);
  }
  const mainAddress = User.getUserMainAddress();
  let myAddress;
  if (address) {
    let type = '';
    switch (address.type) {
      case 'home':
        type = 0;
        break;
      case 'work':
        type = 1;
        break;
      case 'other':
        type = 2;
        break;
      default:
        type = 2;
        break;
    }
    myAddress = {
      street: address.street,
      streetNo: address.streetNo,
      isMainAddress: guestUser ? true : mainAddress._id === address._id,
      floor: address.floor,
      nameAtBell: address.nameAtBell,
      postalCode: address.postalCode,
      city: address.city,
      phone: guestUser ? address.contactPhone : seperateCountryCode(address.contactPhone).phoneNumber,
      addressType: type,
      latitude: address.latitude,
      longitude: address.longitude,
    };
  } else {
    myAddress = {};
  }

  useEffect(() => {
    const CountryCodeStore$ = CountryCodeStore.subscribe(setCountryCode);
    const $shop = Shop.subscribe(shopData => {
      if (shopData?.selectedShop) {
        setDefaultMapCenter({
          lat: shopData.selectedShop.address.latitude,
          lon: shopData.selectedShop.address.longitude,
        });
      }

      if (shopData?.takeAway) {
        // TODO: Optimize this
        setDefaultMapCenter({
          lat: shopData.takeAway[0].address.latitude,
          lon: shopData.takeAway[0].address.longitude,
        });
      }
    });

    if (!guestUser) {
      const cc = seperateCountryCode(address ? address.contactPhone : '').countryCode;

      address && cc !== '' && setCountryCode([cc]);
    }

    return () => {
      CountryCodeStore$.unsubscribe();
      $shop.unsubscribe();
    };
  }, []);

  const onCountryCodeClick = () => {
    CountryCodeSideSheetStore.open();
  };

  const formToAddress = addresses => {
    const newAddress = { ...addresses };
    delete newAddress.isMainAddress;
    newAddress.contactPhone = newAddress.countryOption + newAddress.phone;

    delete newAddress.phone;
    delete newAddress.countryOption;
    switch (newAddress.addressType) {
      case 0:
        newAddress.type = 'home';
        break;
      case 1:
        newAddress.type = 'work';
        break;
      case 2:
        newAddress.type = 'other';
        break;
    }
    delete newAddress.addressType;
    return newAddress;
  };

  async function addAddress(newAddress) {
    setIsNotEligibleText('');
    setIsNotEligible(false);
    const isMain = newAddress.isMainAddress;
    const formated = formToAddress(newAddress);
    const addressToCreate = { address: [{ ...formated }] };
    newAddress.type = getAddressType(newAddress.addressType);
    newAddress.contactPhone = newAddress.phone;
    if (newAddress.type === undefined) newAddress.type = 'home';
    let fieldsPopullated = !hasEmptyKey(newAddress);
    if (!fieldsPopullated && !process.env.hasStoreNecessary) {
      setIsNotEligibleText(t('user_address.edit_fields_error'));
      setIsNotEligible(true);
    }
    if (process.env.hasStoreNecessary) {
      const shopsV3 = await getShopsV3(selectedService?.id, newAddress.postalCode);
      if (!shopsV3?.shops || shopsV3?.shops?.length === 0) {
        router.push('/no-delivery');
        return;
      }
    }
    if (
      (process.env.hasStoreNecessary &&
        newAddress?.postalCode?.length !== 0 &&
        newAddress?.street?.length !== 0 &&
        newAddress?.streetNo?.length !== 0) ||
      (fieldsPopullated && !process.env.hasStoreNecessary)
    ) {
      const response = await createUserAddress(token, newAddress);
      if (response?.data) {
        // setAddresses(prevAddresses => [...prevAddresses, response?.data]);
        const shop = await setShopId(newAddress?.postalCode, selectedService?.id);
        if (!shop) {
          router.push('/no-delivery');
          return;
        }
        setSelectedUserAddressIndex(user?.addresses?.length ? user?.addresses?.length : 0);
        IsFromLoginStore.update(true);
        router.push({
          pathname: '/',
        });
      } else {
        setIsNotEligibleText(response.response?.data?.userMessage ?? ADDRESS_ERROR_MESSSAGE);
        setIsNotEligible(true);
      }
    } else {
      setIsNotEligibleText(t('user_address.edit_fields_error'));
      setIsNotEligible(true);
    }
  }

  return (
    <div className={styles.outerContainer}>
      <Heading
        color={TextColorEnum.Gray1}
        type={HeadingEnum.HeadingS}
        classOption={styles.title}
        style={{ fontWeight: 700 }}
      >
        {t('common.asides.address_view.header_add')}
      </Heading>
      <AddressView
        footer={true}
        footerButtonWording={t('common.asides.address_view.save')}
        footerOnClick={e => {
          addAddress(e);
        }}
        googleApiKey={GOOGLE_API_KEY}
        address={myAddress}
        type={MapPinEnum.Profile}
        hasMainAddressOption={guestUser ? false : process.env.hasMainAddressOption}
        mainAddressWording={t('common.asides.address_view.main_address')}
        notEligibleWording={
          isNotEligibleText.length > 0 ? isNotEligibleText : t('common.asides.address_view.not_eligible')
        }
        setIsNotEligible={setIsNotEligible}
        onCodeClick={onCountryCodeClick}
        onOpenSideClick={onCountryCodeClick}
        addressTypeWording={t('common.asides.address_view.address_kind')}
        selectedCodeIndex={0}
        countryCodeLabel={t('common.asides.address_view.country_code')}
        countries={countryCode}
        isNotEligible={isNotEligible}
        formLabels={{
          address: t('common.asides.address_view.form_labels.address'),
          city: t('common.asides.address_view.form_labels.area'),
          street: t('common.asides.address_view.form_labels.road'),
          streetNo: t('common.asides.address_view.form_labels.street_no'),
          contactPhone: t('common.asides.address_view.form_labels.tel'),
          floor: t('common.asides.address_view.form_labels.floor'),
          nameAtBell: t('common.asides.address_view.form_labels.door_bell'),
          postalCode: t('common.asides.address_view.form_labels.postal'),
        }}
        dropdownArgs={[
          t('common.asides.service_address.types.home'),
          t('common.asides.service_address.types.work'),
          t('common.asides.service_address.types.other'),
        ]}
        minLengthErrorText={t('common.minimun_character')}
        addressConfig={config?.fields?.address ?? null}
        mapTilesLocale={router.locale}
        // TODO: Change this when it's in the api configuration
        restrictAutoCompleteToCountry={process.env.defaultAutocompleteCountryCode}
        defaultCenter={defaultMapCenter}
        requiredText={t('common.asides.address_view.form_labels.required_field')}
        invalidText={t('common.asides.address_view.form_labels.invalid_field')}
      />
      <CountryCodeAside />
    </div>
  );
}
